import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * VknLookupField — VKN (Vergi Kimlik No) input + otomatik firma sorgu butonu.
 *
 * Kullanımı:
 *   <VknLookupField
 *     value={form.tax_number}
 *     onChange={(v) => setForm(p => ({...p, tax_number: v}))}
 *     onCompanyFound={(company) => setForm(p => ({...p, name: company.title}))}
 *     testId="new-customer-tax"
 *   />
 *
 * "Sorgula" tıklandığında GET /api/tax-lookup/{vkn} çağrılır.
 * Kayıt bulunursa `onCompanyFound(company)` çağrısı yapılır; component üstteki
 * form'a unvan bilgisini otomatik doldurma imkanı sağlar.
 *
 * GİB e-Fatura mükellef listesi Ayarlar → Uygulama → GİB Listesi bölümünden
 * import edilmelidir. Kayıt yoksa uyarı gösterir.
 */
export default function VknLookupField({
  value = "",
  onChange,
  onCompanyFound,
  placeholder = "10 haneli VKN veya 11 haneli TCKN",
  testId = "vkn-input",
  disabled = false,
  className = "",
}) {
  const [looking, setLooking] = useState(false);
  const [status, setStatus] = useState(null); // {ok, title, note}

  const lookup = async () => {
    const v = (value || "").trim().replace(/\s|-/g, "");
    if (!v) { toast.error("Önce VKN yazın"); return; }
    if (!/^\d{10,11}$/.test(v)) {
      toast.error("VKN 10, TCKN 11 haneli olmalı");
      return;
    }
    setLooking(true);
    setStatus(null);
    try {
      const { data } = await api.get(`/tax-lookup/${v}`);
      setStatus({ ok: true, title: data.title });
      onCompanyFound?.(data);
      toast.success(`Bulundu: ${data.title}`);
    } catch (e) {
      if (e?.response?.status === 404) {
        setStatus({ ok: false, note: "Kayıt yok — manuel girin" });
        toast.warning("Bu VKN e-fatura listesinde yok. Manuel yazabilirsiniz.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setLooking(false); }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-stretch gap-1.5">
        <Input
          value={value || ""}
          onChange={(e) => { onChange?.(e.target.value); setStatus(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }}
          placeholder={placeholder}
          disabled={disabled || looking}
          className="rounded-sm h-9 font-mono flex-1"
          data-testid={testId}
        />
        <Button
          type="button"
          onClick={lookup}
          disabled={disabled || looking || !value}
          className="rounded-sm h-9 px-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-40"
          data-testid={`${testId}-lookup`}
          title="GİB e-fatura mükellef listesinden sorgula"
        >
          {looking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
        </Button>
      </div>
      {status && status.ok && (
        <div className="text-[11px] font-mono text-emerald-500 flex items-center gap-1" data-testid={`${testId}-result-ok`}>
          <CheckCircle2 className="w-3 h-3" /> {status.title}
        </div>
      )}
      {status && !status.ok && (
        <div className="text-[11px] font-mono text-amber-500" data-testid={`${testId}-result-none`}>
          ⚠ {status.note}
        </div>
      )}
    </div>
  );
}
